import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  CardExpiryElementComponent,
  CardCvcElementComponent,
  CardNumberElementComponent,
} from '@stripe/react-stripe-js';
import { Box } from '@chakra-ui/react';
import { cx } from '@chakra-ui/utils';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
} from '@chakra-ui/form-control';

import { useCommonOptionsStore } from '../../store';
import { useTranslations } from '../../translations/useTranslations';

import { CardInputControlProps } from './types';

type InputType = 'card' | 'cvc' | 'expiry';
type InputElementType = CardNumberElementComponent | CardCvcElementComponent | CardExpiryElementComponent;

const InputTypes: Record<InputType, InputElementType> = {
  card: CardNumberElement,
  cvc: CardCvcElement,
  expiry: CardExpiryElement,
};

export const StripeInputControl = React.forwardRef<HTMLInputElement, any>(({
  id,
  name: fieldName,
  label,
  helper,
  error,
  isDisabled,
  isRequired,
  inputType,
  theme,
  onChange,
  onBlur,
// eslint-disable-next-line @typescript-eslint/no-unused-vars
}: CardInputControlProps & { inputType: InputType }, forwardRef) => {
  const {
    register,
    setValue,
    setError,
    getValues,
    clearErrors,
    formState,
  } = useFormContext();

  const lang = useCommonOptionsStore(state => state.lang);
  const [t] = useTranslations(lang);

  const registerOptions = {
    validate: {
      nativeValidation: () => (formState.errors[fieldName!]?.message as string),
      required: (val: string) => (!isRequired || !!val) || t('requiredField'),
    },
  };

  register(fieldName!, { ...registerOptions });

  const StripeElement = InputTypes[inputType];

  return (
    <Box>
      <FormControl
        isInvalid={!!error}
        isDisabled={isDisabled}
        isRequired={isRequired}
        className={cx('InputContainer')}
      >
        <FormLabel className={cx('InputLabel')}>
          {label}
        </FormLabel>

        <StripeElement
          id={id}
          onChange={(event: any) => {
            setValue(fieldName!, true);
            if (event.error?.message) setError(fieldName!, { type: 'validation', message: event.error?.message });
            else clearErrors(fieldName);
            if (onChange) onChange(fieldName as any);
          }}
          onBlur={() => {
            if (!getValues()[fieldName!]) setError(fieldName!, { type: 'validation', message: t('requiredField') });
            if (onBlur) onBlur(fieldName as any);
          }}
          options={theme}
        />

        {helper && <FormHelperText className={cx('InputHelper')}>{helper}</FormHelperText>}
        {error && <FormErrorMessage className={cx('InputError')}>{error}</FormErrorMessage>}
      </FormControl>
    </Box>
  );
});
